import {
  getFileIcon,
  getIconColor,
} from "@/components/Chat/input/components/FileMentionDropdown";
import { TEST_IDS } from "@/constants/testIds";
import { supabase } from "@/lib";
import PropTypes from "prop-types";
import { memo, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getFriendlyFileType } from "../utils/fileTypeUtils";
import { extractCleanFileName } from "../utils/messageUtils";

// Global cache for file attachment renders and image URLs
const fileAttachmentCache = new Map();
const imageUrlCache = new Map();

// Generate short UUID-like ID from string (same hash function as library)
const generateShortId = (str) => {
  if (!str) return "";
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(36).substring(0, 8);
};

// Simplified FileAttachment component that works with minimal file information
const FileAttachment = memo(
  function FileAttachment({ file, isImage, messageId, threadId }) {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [imageUrl, setImageUrl] = useState(null);
    const [hasLoaded, setHasLoaded] = useState(false);
    const mountedRef = useRef(true);
    const hasProcessedRef = useRef(false);
    const loadingRef = useRef(false);
    const fileKey = `${file?.id || file?.path || file?.name || "file"}-${isImage ? "image" : "doc"}`;

    // Navigation for lightbox
    const navigate = useNavigate();
    const location = useLocation();

    // Process file only once on mount
    useEffect(() => {
      // Check if we've already cached this file
      if (fileAttachmentCache.has(fileKey)) {
        hasProcessedRef.current = true;
        return;
      }

      // Mark this file as processed
      hasProcessedRef.current = true;
      fileAttachmentCache.set(fileKey, true);

      return () => {
        mountedRef.current = false;
      };
    }, [fileKey]);

    // Load image preview for image files - only run once
    useEffect(() => {
      if (
        !isImage ||
        !file ||
        (!file.path && !file.name && !file.url) ||
        !mountedRef.current ||
        hasLoaded ||
        loadingRef.current
      ) {
        return;
      }

      // Check if this is a direct URL (external or generated image)
      if (
        file.url &&
        (file.url.startsWith("http") || file.isExternalUrl || file.isGenerated)
      ) {
        // Use URL directly for external/generated images
        const cacheKey = `${file.url}-preview`;
        if (imageUrlCache.has(cacheKey)) {
          const cachedUrl = imageUrlCache.get(cacheKey);
          setImageUrl(cachedUrl);
          setHasLoaded(true);
          return;
        }

        setImageUrl(file.url);
        setHasLoaded(true);
        imageUrlCache.set(cacheKey, file.url);
        return;
      }

      const filePath = file.path || file.name;
      const cacheKey = `${filePath}-preview`;

      // Check if we already have this image cached
      if (imageUrlCache.has(cacheKey)) {
        const cachedUrl = imageUrlCache.get(cacheKey);
        setImageUrl(cachedUrl);
        setHasLoaded(true);
        return;
      }

      const loadImagePreview = async () => {
        if (loadingRef.current) return; // Prevent concurrent loads

        try {
          loadingRef.current = true;
          setIsLoading(true);
          setError(null);

          // First try to get public URL if the file is in a public bucket
          try {
            // The filePath should already include the user ID if it was uploaded with the new system
            const { data: publicUrlData } = supabase.storage
              .from("chat")
              .getPublicUrl(filePath);

            if (publicUrlData?.publicUrl) {
              // Test if the public URL is accessible
              const response = await fetch(publicUrlData.publicUrl, {
                method: "HEAD",
              });
              if (response.ok) {
                if (mountedRef.current) {
                  setImageUrl(publicUrlData.publicUrl);
                  setHasLoaded(true);
                  imageUrlCache.set(cacheKey, publicUrlData.publicUrl);
                  setIsLoading(false);
                }
                return;
              }
            }
          } catch (publicUrlError) {
            console.error("Public URL not accessible, trying download method");
          }

          // If public URL doesn't work, try downloading the file
          // The filePath should already include the user ID if it was uploaded with the new system
          const { data: fileData, error: downloadError } =
            await supabase.storage.from("chat").download(filePath);

          if (downloadError) {
            throw downloadError;
          }

          if (fileData && mountedRef.current) {
            // Create blob URL for the downloaded image
            const blob = new Blob([fileData], {
              type: file.type || "image/jpeg",
            });
            const blobUrl = URL.createObjectURL(blob);
            setImageUrl(blobUrl);
            setHasLoaded(true);
            imageUrlCache.set(cacheKey, blobUrl);
          }
        } catch (err) {
          console.error("Error loading image preview:", err);
          if (mountedRef.current) {
            setError(err.message);
            setHasLoaded(true); // Mark as loaded even on error to prevent retries
          }
        } finally {
          if (mountedRef.current) {
            setIsLoading(false);
          }
          loadingRef.current = false;
        }
      };

      loadImagePreview();
    }, [isImage, file?.path, file?.name, file?.type, hasLoaded]); // Removed imageUrl from dependencies

    // Cleanup on unmount
    useEffect(() => {
      return () => {
        mountedRef.current = false;
        // Only cleanup blob URLs, not cached URLs
        if (
          imageUrl &&
          imageUrl.startsWith("blob:") &&
          !imageUrlCache.has(`${file?.path || file?.name}-preview`)
        ) {
          URL.revokeObjectURL(imageUrl);
        }
      };
    }, []); // Empty dependency array - only run on unmount

    // Return null for invalid file data to prevent rendering issues
    if (!file || (!file.name && !file.path)) return null;

    // Only use the clean file name, never display the full path
    const fileExtension = file.name?.split(".")?.pop()?.toLowerCase() || "";
    const cleanFileName = extractCleanFileName(
      file.name || file.path || "Unknown file"
    );

    const handleDownload = async (e) => {
      e.stopPropagation();

      try {
        // Extract bucket and file path from the storage path
        // Format: "thread_da6806ec_origname=artifact_key_1749579436278_429854.txt"
        const filePath = file.path || file.name;

        if (filePath && supabase) {
          // Download file from Supabase storage
          // The filePath should already include the user ID if it was uploaded with the new system
          const { data, error } = await supabase.storage
            .from("chat") // Using 'chat' bucket based on the data structure
            .download(filePath);

          if (error) {
            console.error("Error downloading file from Supabase:", error);
            // Fallback to direct URL download
            fallbackDownload();
            return;
          }

          // Create blob URL and trigger download
          const blob = new Blob([data], {
            type: file.type || "application/octet-stream",
          });
          const url = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = cleanFileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);
        } else {
          fallbackDownload();
        }
      } catch (error) {
        console.error("Error in download handler:", error);
        fallbackDownload();
      }

      function fallbackDownload() {
        // Fallback to direct URL download
        if (file.path || file.url || file.publicUrl) {
          const downloadUrl = file.url || file.publicUrl || file.path;
          const link = document.createElement("a");
          link.href = downloadUrl;
          link.download = cleanFileName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      }
    };

    // For images, render the actual image preview or a placeholder
    if (isImage || file.isImage) {
      return (
        <div
          className="group overflow-hidden rounded-2xl relative bg-gray-100/20 dark:bg-zinc-800/30 border border-gray-200/30 dark:border-zinc-700/40 shadow-sm hover:bg-gray-100/40 dark:hover:bg-zinc-800/40 transition-colors duration-200 max-w-64 max-h-64 cursor-pointer"
          // Stop propagation of all mouse events to prevent parent re-renders
          onMouseEnter={(e) => e.stopPropagation()}
          onMouseLeave={(e) => e.stopPropagation()}
          onMouseOver={(e) => e.stopPropagation()}
          onMouseMove={(e) => e.stopPropagation()}
          onClick={(e) => {
            e.stopPropagation();
            if (imageUrl) {
              // Generate a unique ID for this image based on file path/name
              const imageId = file?.id || file?.path || file?.name || "user-upload";
              const shortId = generateShortId(imageId);

              // Navigate to lightbox with state for instant modal rendering
              navigate(`/library/image/${shortId}`, {
                state: {
                  backgroundLocation: location,
                  imageData: {
                    id: imageId,
                    url: imageUrl,
                    thumbnailUrl: imageUrl,
                    name: cleanFileName,
                    prompt: cleanFileName, // Use filename as prompt/title for user uploads
                    messageId: messageId,
                    threadId: threadId, // For navigation between all thread images
                    isUserUpload: true,
                    toolName: "storage", // Indicates this is from storage, not generated
                    imageProvider: "User Upload",
                    imageModel: null,
                    createdAt: file?.created_at || file?.lastModified ? new Date(file.lastModified).toISOString() : new Date().toISOString(),
                  },
                },
              });
            }
          }}
        >
          {/* Loading state */}
          {isLoading && !hasLoaded && (
            <div className="flex items-center justify-center h-full w-full">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-500"></div>
            </div>
          )}

          {/* Error state */}
          {error && hasLoaded && !imageUrl && (
            <div className="flex items-center justify-center h-full w-full p-3">
              <div className="flex items-center">
                <div className="flex-shrink-0 text-red-500 dark:text-red-400 mr-3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="15" y1="9" x2="9" y2="15"></line>
                    <line x1="9" y1="9" x2="15" y2="15"></line>
                  </svg>
                </div>
                <div className="text-sm dark:text-zinc-300 text-zinc-800 font-medium">
                  {cleanFileName}
                  <div className="text-xs text-red-500 mt-1">
                    Failed to load image
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Image preview */}
          {imageUrl && hasLoaded && (
            <div className="relative flex items-center justify-center">
              <img
                src={imageUrl}
                alt={cleanFileName}
                className="max-w-64 max-h-64 w-auto h-auto object-contain rounded-lg"
                onError={(e) => {
                  console.error("Image failed to load:", imageUrl);
                  if (mountedRef.current) {
                    setError("Failed to display image");
                  }
                }}
                onLoad={() => {
                  // Image loaded successfully
                  if (mountedRef.current) {
                    setError(null);
                  }
                }}
              />
            </div>
          )}

          {/* Fallback placeholder when no image URL and not loading */}
          {!imageUrl && !isLoading && hasLoaded && !error && (
            <div className="flex items-center justify-center h-full w-full p-3">
              <div className="flex items-center">
                <div className="flex-shrink-0 text-purple-500 dark:text-purple-400 mr-3">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect
                      x="3"
                      y="3"
                      width="18"
                      height="18"
                      rx="2"
                      ry="2"
                    ></rect>
                    <circle cx="8.5" cy="8.5" r="1.5"></circle>
                    <polyline points="21 15 16 10 5 21"></polyline>
                  </svg>
                </div>
                <div className="text-sm dark:text-zinc-300 text-zinc-800 font-medium">
                  {cleanFileName}
                </div>
              </div>
            </div>
          )}

          {/* Download button for images - appears on hover */}
          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <button
              onClick={handleDownload}
              className="p-1.5 rounded-lg bg-black/50 hover:bg-black/70 transition-colors duration-200 text-white"
              title="Download image"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            </button>
          </div>
        </div>
      );
    }

    // For regular files, show minimal file info with icon
    const FileIcon = getFileIcon(file.name || file.path || "unknown");
    const iconColor = getIconColor(file.name || file.path || "unknown");
    const isPdf =
      fileExtension === "pdf" ||
      file.isPdf ||
      (file.type && file.type.includes("pdf"));
    const isDocument =
      file.isDocument ||
      (file.type && (file.type.includes("doc") || file.type.includes("text")));

    return (
      <div
        className="group flex items-center py-2 px-3 rounded-2xl border border-gray-200/30 dark:border-zinc-700/40 bg-gray-100/20 dark:bg-zinc-800/30 hover:bg-gray-100/40 dark:hover:bg-zinc-800/40 transition-colors duration-200 shadow-sm w-full relative"
        data-testid={TEST_IDS.FILE_ATTACHMENT}
        // Stop propagation of all mouse events to prevent parent re-renders
        onMouseEnter={(e) => e.stopPropagation()}
        onMouseLeave={(e) => e.stopPropagation()}
        onMouseOver={(e) => e.stopPropagation()}
        onMouseMove={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex-shrink-0 mr-3">
          {isPdf ? (
            <div className="relative inline-block">
              <FileIcon className={`h-5 w-5 ${iconColor}`} />
              <span className="absolute -bottom-1 -right-1 text-[6px] font-bold text-red-500 bg-white dark:bg-zinc-900 rounded-full px-0.5">
                PDF
              </span>
            </div>
          ) : (
            <FileIcon className={`h-5 w-5 ${iconColor}`} />
          )}
        </div>

        <div className="min-w-0 flex-1 relative">
          <div className="text-sm text-foreground truncate font-medium">
            {cleanFileName}
          </div>
          {file.type && (
            <div className="text-xs text-muted-foreground truncate">
              {file.friendlyType || getFriendlyFileType(file.type, file.name)}
            </div>
          )}

          {/* Download button - appears on hover, overlays the text */}
          <div className="absolute top-0 right-0 bottom-0 flex items-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
            <button
              onClick={handleDownload}
              className="p-1.5 rounded-lg bg-transparent hover:bg-gray-200/30 dark:hover:bg-zinc-700/30 transition-colors duration-200 text-white hover:text-white"
              title="Download file"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison to prevent unnecessary re-renders

    // Different file types always trigger re-render
    if (prevProps.isImage !== nextProps.isImage) return false;

    // Compare file IDs if available
    if (prevProps.file?.id && nextProps.file?.id) {
      return prevProps.file.id === nextProps.file.id;
    }

    // Compare paths if available
    if (prevProps.file?.path && nextProps.file?.path) {
      return prevProps.file.path === nextProps.file.path;
    }

    // Compare names if available
    if (prevProps.file?.name && nextProps.file?.name) {
      return prevProps.file.name === nextProps.file.name;
    }

    // If we get here, do a more expensive comparison
    return JSON.stringify(prevProps.file) === JSON.stringify(nextProps.file);
  }
);

FileAttachment.propTypes = {
  file: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    path: PropTypes.string,
    type: PropTypes.string,
    isPdf: PropTypes.bool,
    isDocument: PropTypes.bool,
    isImage: PropTypes.bool,
  }),
  isImage: PropTypes.bool,
  messageId: PropTypes.string,
  threadId: PropTypes.string,
};

export default FileAttachment;
